import { DSE_CORE_SUBJECT_CODES, normalizeSubjectCode } from './dseSubjects';

const LEGACY_SCORE_KEYS = {
  'Chinese Language': 'CHI',
  'English Language': 'ENG',
  'Mathematics Compulsory Part': 'MAT',
};

export const MAX_SURVEY_ELECTIVE_SUBJECTS = 4;
export const MAX_SURVEY_RANKING_PROGRAMMES = 20;

const CORE_CODE_SET = new Set(DSE_CORE_SUBJECT_CODES);

function isCoreSubjectCode(code) {
  return CORE_CODE_SET.has(normalizeSubjectCode(code));
}

export function capScoreMapElectives(scoreMap, maxElectives = MAX_SURVEY_ELECTIVE_SUBJECTS, preferredElectiveOrder = []) {
  const core = {};
  const electives = {};

  Object.entries(scoreMap || {}).forEach(([code, score]) => {
    if (score == null || score === '') return;
    const normalized = normalizeSubjectCode(code);
    if (!normalized) return;
    if (isCoreSubjectCode(normalized)) {
      core[normalized] = String(score);
      return;
    }
    electives[normalized] = String(score);
  });

  const orderedElectives = [];
  preferredElectiveOrder.forEach((code) => {
    const normalized = normalizeSubjectCode(code);
    if (electives[normalized] && !orderedElectives.includes(normalized)) {
      orderedElectives.push(normalized);
    }
  });
  Object.keys(electives).forEach((code) => {
    if (!orderedElectives.includes(code)) orderedElectives.push(code);
  });

  const cappedElectives = Object.fromEntries(
    orderedElectives.slice(0, maxElectives).map((code) => [code, electives[code]])
  );

  return { ...core, ...cappedElectives };
}

export function normalizeAccountScoreMap(scoresObj) {
  const map = {};
  if (!scoresObj || typeof scoresObj !== 'object') return map;

  Object.entries(scoresObj).forEach(([key, value]) => {
    if (value == null || value === '') return;
    if (['Core Subject', 'Core Subject Code', 'Core Score'].includes(key)) return;
    const code = normalizeSubjectCode(LEGACY_SCORE_KEYS[key] || key);
    if (!code) return;
    map[code] = String(value);
  });

  return map;
}

export function scoreRowsToMap(rows) {
  const map = {};
  (rows || []).forEach((row) => {
    if (!row?.subjectCode || !row?.score) return;
    map[normalizeSubjectCode(row.subjectCode)] = String(row.score);
  });
  return map;
}

export function mapToScoreRows(map) {
  const capped = capScoreMapElectives(map);
  return Object.entries(capped)
    .filter(([code, score]) => code && score)
    .map(([subjectCode, score]) => ({ subjectCode, score }));
}

export function detectScoreConflicts(localMap, accountMap) {
  const conflicts = [];
  const codes = new Set([...Object.keys(localMap), ...Object.keys(accountMap)]);

  codes.forEach((code) => {
    const local = localMap[code];
    const account = accountMap[code];
    if (local && account && local !== account) {
      conflicts.push({ code, local, account });
    }
  });

  return conflicts;
}

export function mergeScoreMaps(localMap, accountMap, resolution = 'fill-empty') {
  let merged;
  if (resolution === 'account') {
    merged = { ...localMap, ...accountMap };
  } else if (resolution === 'local') {
    merged = { ...accountMap, ...localMap };
  } else {
    merged = { ...accountMap };
    Object.entries(localMap).forEach(([code, score]) => {
      if (score) merged[code] = score;
    });
  }

  const localElectiveOrder = Object.keys(localMap).filter((code) => !isCoreSubjectCode(code));
  return capScoreMapElectives(merged, MAX_SURVEY_ELECTIVE_SUBJECTS, localElectiveOrder);
}

export function isScoreMapEmpty(map) {
  return !map || Object.keys(map).length === 0;
}

export async function fetchAccountScoreMap() {
  const token = window.localStorage.getItem('token');
  if (!token) return null;

  const response = await fetch('/api/dse-scores/load', {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) return null;
  const payload = await response.json();
  return capScoreMapElectives(normalizeAccountScoreMap(payload?.scores));
}

export async function saveAccountScoreMap(scoreMap) {
  const token = window.localStorage.getItem('token');
  if (!token) return false;

  const cappedScores = capScoreMapElectives(scoreMap);

  const response = await fetch('/api/dse-scores/save', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ scores: cappedScores }),
  });

  return response.ok;
}

export function mergeCommentFromUser(comment, user) {
  if (!user) return comment;
  const name = [user.Nickname, user.First_Name, user.Last_Name].filter(Boolean).join(' ').trim();
  if (!name || comment.volunteerName) return comment;
  return { ...comment, volunteerName: name };
}

export function shouldFillRankingFromAccount(localRanking) {
  return !localRanking?.length;
}

export function rankingToProgrammeOrder(ranking) {
  const savedOrder = {};
  (ranking || []).forEach((programme, index) => {
    const code = String(programme?.code || '').trim().toUpperCase();
    if (code) savedOrder[code] = index;
  });
  return savedOrder;
}

export function programmeOrderToCodes(programmeOrder, maxProgrammes = MAX_SURVEY_RANKING_PROGRAMMES) {
  return Object.entries(programmeOrder || {})
    .sort(([, posA], [, posB]) => Number(posA) - Number(posB))
    .map(([code]) => String(code || '').trim().toUpperCase())
    .filter(Boolean)
    .slice(0, maxProgrammes);
}

export async function fetchAccountProgrammeOrder() {
  const token = window.localStorage.getItem('token');
  if (!token) return null;

  const response = await fetch('/api/jupas-order/load', {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) return null;
  const payload = await response.json();
  return payload?.programmeOrder && typeof payload.programmeOrder === 'object'
    ? payload.programmeOrder
    : {};
}

export async function saveAccountProgrammeOrder(ranking) {
  const token = window.localStorage.getItem('token');
  if (!token) return false;

  const programmeOrder = rankingToProgrammeOrder(
    (ranking || []).slice(0, MAX_SURVEY_RANKING_PROGRAMMES)
  );
  if (!Object.keys(programmeOrder).length) return false;

  const response = await fetch('/api/jupas-order/save', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ programmeOrder }),
  });

  return response.ok;
}

export async function resolveRankingProgrammes(programmeOrder, collection, maxProgrammes = MAX_SURVEY_RANKING_PROGRAMMES) {
  const codes = programmeOrderToCodes(programmeOrder, maxProgrammes);
  if (!codes.length || !collection) return [];

  const response = await fetch(`/api/programmes/${encodeURIComponent(collection)}/codes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ codes }),
  });
  const result = await response.json();
  if (!response.ok || !result.success) return [];

  return codes
    .map((code) => result.programmes_by_code?.[code] || result.programmes_by_code?.[code.toUpperCase()])
    .filter(Boolean);
}
